(function () {
  const WHOLESALE_TIERS = [20, 10, 5];

  function storefrontRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  }

  function normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-UY', {
        style: 'currency',
        currency: currency || 'UYU',
        maximumFractionDigits: 0,
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return '$' + Math.round(Number(cents || 0) / 100).toLocaleString('es-UY');
    }
  }

  function cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
      .filter(Boolean);
  }

  async function currentCart() {
    const response = await fetch(storefrontRoot() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('No se pudo actualizar el carrito. Intentá nuevamente.');
    return response.json();
  }

  function notifyThemeCart(cart, sections, lines, source) {
    const promise = Promise.resolve({
      cart: { totalQuantity: Number(cart.item_count) || 0 },
      detail: {
        didError: false,
        itemCount: Number(cart.item_count) || 0,
        items: cart.items || [],
        sections: sections || {},
        source: source || 'deusdream-commerce',
      },
    });
    const event = new Event('shopify:cart:lines-update', { bubbles: true });
    Object.defineProperties(event, {
      action: { value: 'update' },
      context: { value: 'cart' },
      lines: { value: lines || [] },
      promise: { value: promise },
    });
    document.dispatchEvent(event);
  }

  async function addItems(items, source) {
    const validItems = items
      .map((item) => ({
        id: Number(item.id),
        quantity: Math.max(0, Math.floor(Number(item.quantity) || 0)),
        ...(item.properties && Object.keys(item.properties).length ? { properties: item.properties } : {}),
      }))
      .filter((item) => item.id && item.quantity > 0);

    if (!validItems.length) throw new Error('Elegí al menos una unidad para continuar.');

    const sectionIds = cartSectionIds();
    const response = await fetch(storefrontRoot() + 'cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: validItems,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });

    let result;
    try {
      result = await response.json();
    } catch (_error) {
      result = {};
    }

    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'No se pudo agregar la selección. Intentá nuevamente.');
    }

    const cart = await currentCart();
    notifyThemeCart(
      cart,
      result.sections,
      validItems.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      source
    );
    return cart;
  }

  function isWholesaleProduct(product) {
    const tags = Array.isArray(product.tags) ? product.tags.join(' ') : product.tags;
    const descriptor = normalize([product.title, product.handle, product.type, tags].join(' '));
    return descriptor.includes('mayorista') || descriptor.includes('producto-mayorista') || descriptor.includes('combo-mayorista');
  }

  function recommendationReason(anchorTitle, product) {
    const anchor = normalize(anchorTitle);
    const complement = normalize([product.title, product.type, ...(product.tags || [])].join(' '));

    if (anchor.includes('magsafe') && /(adaptador|20 ?w|cargador)/.test(complement)) {
      return 'A este MagSafe le falta un adaptador de 20 W para funcionar correctamente.';
    }
    if (/(cable|usb|lightning|tipo c|type c)/.test(anchor) && /(adaptador|cargador|20 ?w)/.test(complement)) {
      return 'Este cable se completa con el adaptador correcto para que puedas usarlo desde el primer día.';
    }
    if (/(adaptador|cargador|power ?bank)/.test(anchor) && /(cable|usb|lightning|tipo c|type c)/.test(complement)) {
      return 'Este cable completa la conexión de tu cargador y evita que te falte una pieza al recibirlo.';
    }
    if (/(iphone|samsung|celular|telefono)/.test(anchor) && /(funda|protector|vidrio|templado)/.test(complement)) {
      return 'Sumá protección desde el primer día y evitá usar tu equipo sin la cobertura adecuada.';
    }
    if (/(funda|protector|vidrio|templado)/.test(anchor) && /(magsafe|cargador|adaptador)/.test(complement)) {
      return 'Completá protección y carga en una sola compra, con un accesorio pensado para acompañar tu elección.';
    }
    if (/(airpod|auricular|jbl|parlante)/.test(anchor) && /(funda|estuche|cable|cargador)/.test(complement)) {
      return 'Es el complemento práctico para proteger o mantener cargado el producto que elegiste.';
    }
    if (/(camara|cámara)/.test(anchor) && /(memoria|tarjeta|soporte|tripode|trípode)/.test(complement)) {
      return 'Completa lo necesario para empezar a usar tu cámara sin tener que hacer otra compra después.';
    }

    return 'Complementa ' + anchorTitle + ' y fue seleccionado específicamente para usarse junto a ese producto.';
  }

  class SmartCartRecommendation extends HTMLElement {
    constructor() {
      super();
      this.abortController = new AbortController();
      this.product = null;
      this.variant = null;
    }

    connectedCallback() {
      if (this.abortController.signal.aborted) this.abortController = new AbortController();
      this.querySelector('[data-dd-smart-add]')?.addEventListener('click', this.handleAction, {
        signal: this.abortController.signal,
      });
      this.querySelector('[data-dd-smart-variant]')?.addEventListener('change', this.handleVariantChange, {
        signal: this.abortController.signal,
      });
      this.load();
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    async load() {
      const anchorId = Number(this.dataset.anchorId);
      if (!anchorId) return;

      const cartProductIds = new Set(
        String(this.dataset.cartProductIds || '')
          .split(',')
          .map(Number)
          .filter(Boolean)
      );

      try {
        const url = new URL(storefrontRoot() + 'recommendations/products.json', window.location.origin);
        url.searchParams.set('product_id', String(anchorId));
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'complementary');

        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: this.abortController.signal,
        });
        if (!response.ok) return;

        const result = await response.json();
        const product = (result.products || []).find((candidate) => {
          const variant = (candidate.variants || []).find((item) => item.available);
          return candidate.available && Number(candidate.price) > 0 && variant &&
            !cartProductIds.has(Number(candidate.id)) && !isWholesaleProduct(candidate);
        });
        if (!product) return;

        const variant = (product.variants || []).find((item) => item.available);
        if (!variant) return;

        this.product = product;
        this.variant = variant;
        this.renderProduct();
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn('[DeusDream] No se pudo cargar el complemento:', error);
      }
    }

    renderProduct() {
      const product = this.product;
      const variant = this.variant;
      if (!product || !variant) return;

      const productUrl = product.url || storefrontRoot() + 'products/' + product.handle;
      const imageSource = typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image?.src || variant.featured_image?.src || '';
      const links = this.querySelectorAll('[data-dd-smart-link], [data-dd-smart-title]');
      links.forEach((link) => link.setAttribute('href', productUrl));

      const image = this.querySelector('[data-dd-smart-image]');
      if (image instanceof HTMLImageElement) {
        if (imageSource) {
          image.src = imageSource;
          image.alt = product.title;
        } else {
          image.hidden = true;
        }
      }

      const title = this.querySelector('[data-dd-smart-title]');
      const price = this.querySelector('[data-dd-smart-price]');
      const installments = this.querySelector('[data-dd-smart-installments]');
      const reason = this.querySelector('[data-dd-smart-reason]');
      if (title) title.textContent = product.title;
      if (price) price.textContent = formatMoney(variant.price, this.dataset.currency);
      if (installments) installments.textContent = '12 cuotas de ' + formatMoney(variant.price / 12, this.dataset.currency) + ' sin interés';
      if (reason) reason.textContent = recommendationReason(this.dataset.anchorTitle || 'tu producto', product);

      const availableVariants = (product.variants || []).filter((item) => item.available);
      const variantField = this.querySelector('[data-dd-smart-variant-field]');
      const variantSelect = this.querySelector('[data-dd-smart-variant]');
      if (availableVariants.length > 1 && variantField instanceof HTMLElement && variantSelect instanceof HTMLSelectElement) {
        variantSelect.replaceChildren();
        for (const availableVariant of availableVariants) {
          const option = document.createElement('option');
          option.value = String(availableVariant.id);
          option.textContent = availableVariant.title + ' · ' + formatMoney(availableVariant.price, this.dataset.currency);
          variantSelect.appendChild(option);
        }
        variantField.hidden = false;
      }

      this.hidden = false;
      const card = this.querySelector('[data-dd-smart-card]');
      if (card instanceof HTMLElement) card.hidden = false;
    }

    handleVariantChange = (event) => {
      const select = event.currentTarget;
      if (!(select instanceof HTMLSelectElement) || !this.product) return;
      const selected = (this.product.variants || []).find((variant) => String(variant.id) === select.value && variant.available);
      if (!selected) return;
      this.variant = selected;
      const price = this.querySelector('[data-dd-smart-price]');
      const installments = this.querySelector('[data-dd-smart-installments]');
      if (price) price.textContent = formatMoney(selected.price, this.dataset.currency);
      if (installments) installments.textContent = '12 cuotas de ' + formatMoney(selected.price / 12, this.dataset.currency) + ' sin interés';
    };

    handleAction = async () => {
      if (!this.product || !this.variant) return;

      const button = this.querySelector('[data-dd-smart-add]');
      const label = this.querySelector('[data-dd-smart-action-label]');
      const status = this.querySelector('[data-dd-smart-status]');
      if (!(button instanceof HTMLButtonElement)) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Agregando…';
      if (status) status.textContent = '';

      try {
        await addItems([{ id: this.variant.id, quantity: 1 }], 'deusdream-smart-recommendation');
        if (label) label.textContent = 'Agregado ✓';
        if (status) status.textContent = 'Listo. Se agregó al carrito sin sacarte de esta página.';
      } catch (error) {
        button.disabled = false;
        if (label) label.textContent = 'Completar mi kit';
        if (status) status.textContent = error?.message || 'No se pudo agregar. Intentá nuevamente.';
      } finally {
        button.removeAttribute('aria-busy');
      }
    };
  }

  function sanitizedProperties(properties) {
    const result = {};
    for (const [key, value] of Object.entries(properties || {})) {
      if (value !== '' && value !== null && value !== undefined) result[key] = value;
    }
    return result;
  }

  function wholesaleProperties(properties, mode, tier, token) {
    const result = sanitizedProperties(properties);
    delete result._dd_wholesale_tier;
    delete result._dd_wholesale_pack_id;
    delete result._dd_wholesale_mode;
    result._dd_wholesale = 'true';
    result._dd_wholesale_mode = mode;
    if (tier) result._dd_wholesale_tier = String(tier);
    if (token) result._dd_wholesale_pack_id = token;
    return result;
  }

  function normalizeReorderItem(item, quantity, itemIndex, timestamp) {
    const properties = sanitizedProperties(item.properties);
    if (!item.isWholesale || item.isCombo || properties._dd_wholesale_combo === 'true' || properties._dd_wholesale_mode === 'combo') {
      return [{ id: item.variantId, quantity, properties }];
    }

    const lines = [];
    let remaining = quantity;
    for (const tier of WHOLESALE_TIERS) {
      const packQuantity = Math.floor(remaining / tier) * tier;
      if (packQuantity > 0) {
        lines.push({
          id: item.variantId,
          quantity: packQuantity,
          properties: wholesaleProperties(properties, 'pack', tier, 'dd-reorder-' + timestamp + '-' + itemIndex + '-' + tier),
        });
        remaining -= packQuantity;
      }
    }
    if (remaining > 0) {
      lines.push({
        id: item.variantId,
        quantity: remaining,
        properties: wholesaleProperties(properties, 'assorted'),
      });
    }
    return lines;
  }

  class ReorderPanel extends HTMLElement {
    constructor() {
      super();
      this.abortController = new AbortController();
      this.data = { items: [] };
    }

    connectedCallback() {
      if (this.abortController.signal.aborted) this.abortController = new AbortController();
      try {
        this.data = JSON.parse(this.querySelector('[data-dd-reorder-data]')?.textContent || '{"items":[]}');
      } catch (_error) {
        this.data = { items: [] };
      }

      const signal = this.abortController.signal;
      document.addEventListener('click', this.handleDocumentClick, { signal });
      this.querySelectorAll('[data-dd-reorder-close]').forEach((button) => {
        button.addEventListener('click', this.close, { signal });
      });
      this.querySelectorAll('[data-dd-reorder-minus], [data-dd-reorder-plus]').forEach((button) => {
        button.addEventListener('click', this.changeQuantity, { signal });
      });
      this.querySelectorAll('[data-dd-reorder-quantity]').forEach((input) => {
        input.addEventListener('input', this.updateSummary, { signal });
        input.addEventListener('change', this.cleanQuantity, { signal });
      });
      this.querySelector('[data-dd-reorder-submit]')?.addEventListener('click', this.submit, { signal });
      this.querySelector('dialog')?.addEventListener('click', this.closeOnBackdrop, { signal });
      this.updateSummary();
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    handleDocumentClick = (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-dd-reorder-open]') : null;
      if (!trigger) return;
      event.preventDefault();
      const dialog = this.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement && !dialog.open) dialog.showModal();
    };

    close = () => {
      const dialog = this.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    };

    closeOnBackdrop = (event) => {
      const dialog = event.currentTarget;
      if (!(dialog instanceof HTMLDialogElement) || event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      const inside = event.clientX >= bounds.left && event.clientX <= bounds.right &&
        event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      if (!inside) this.close();
    };

    changeQuantity = (event) => {
      const button = event.currentTarget;
      if (!(button instanceof HTMLButtonElement)) return;
      const row = button.closest('[data-dd-reorder-row]');
      const input = row?.querySelector('[data-dd-reorder-quantity]');
      if (!(input instanceof HTMLInputElement)) return;
      const direction = button.hasAttribute('data-dd-reorder-plus') ? 1 : -1;
      input.value = String(Math.max(0, Math.floor(Number(input.value) || 0) + direction));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    };

    cleanQuantity = (event) => {
      const input = event.currentTarget;
      if (!(input instanceof HTMLInputElement)) return;
      input.value = String(Math.max(0, Math.floor(Number(input.value) || 0)));
      this.updateSummary();
    };

    selectedItems() {
      const timestamp = Date.now();
      const result = [];
      this.querySelectorAll('[data-dd-reorder-row]').forEach((row) => {
        if (!(row instanceof HTMLElement)) return;
        const itemIndex = Number(row.dataset.ddReorderIndex);
        const item = this.data.items?.[itemIndex];
        const input = row.querySelector('[data-dd-reorder-quantity]');
        const quantity = input instanceof HTMLInputElement ? Math.max(0, Math.floor(Number(input.value) || 0)) : 0;
        if (!item?.available || !item.variantId || quantity < 1) return;
        result.push(...normalizeReorderItem(item, quantity, itemIndex, timestamp));
      });
      return result;
    }

    updateSummary = () => {
      let products = 0;
      let units = 0;
      this.querySelectorAll('[data-dd-reorder-quantity]').forEach((input) => {
        if (!(input instanceof HTMLInputElement) || input.disabled) return;
        const quantity = Math.max(0, Math.floor(Number(input.value) || 0));
        if (quantity > 0) products += 1;
        units += quantity;
      });
      const summary = this.querySelector('[data-dd-reorder-summary]');
      if (summary) summary.textContent = products > 0
        ? products + (products === 1 ? ' producto' : ' productos') + ' · ' + units + (units === 1 ? ' unidad' : ' unidades')
        : 'Elegí al menos un producto para continuar.';
      const submit = this.querySelector('[data-dd-reorder-submit]');
      if (submit instanceof HTMLButtonElement) submit.disabled = units === 0;
    };

    submit = async () => {
      const items = this.selectedItems();
      const button = this.querySelector('[data-dd-reorder-submit]');
      const label = this.querySelector('[data-dd-reorder-submit-label]');
      const status = this.querySelector('[data-dd-reorder-status]');
      const successActions = this.querySelector('[data-dd-reorder-success-actions]');
      if (!(button instanceof HTMLButtonElement)) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Agregando pedido…';
      if (status) status.textContent = '';
      if (successActions instanceof HTMLElement) successActions.hidden = true;

      try {
        const cart = await addItems(items, 'deusdream-reorder');
        const addedUnits = items.reduce((total, item) => total + Number(item.quantity || 0), 0);
        if (label) label.textContent = 'Pedido agregado ✓';
        if (status) status.textContent = 'Listo: agregamos ' + addedUnits + (addedUnits === 1 ? ' unidad' : ' unidades') + '. Tu carrito ahora tiene ' + cart.item_count + '.';
        if (successActions instanceof HTMLElement) successActions.hidden = false;
      } catch (error) {
        button.disabled = false;
        if (label) label.textContent = 'Agregar selección al carrito';
        if (status) status.textContent = error?.message || 'No se pudo agregar la selección. Intentá nuevamente.';
      } finally {
        button.removeAttribute('aria-busy');
      }
    };
  }

  if (!customElements.get('dd-smart-cart-recommendation')) {
    customElements.define('dd-smart-cart-recommendation', SmartCartRecommendation);
  }
  if (!customElements.get('dd-reorder-panel')) {
    customElements.define('dd-reorder-panel', ReorderPanel);
  }
})();
